import { useState, useEffect } from 'react';
import { crmService } from '../../services/crmService';
import SearchableSelect from '../common/SearchableSelect';
import PhotoUpload from './PhotoUpload';

const MILITARY_RANKS = [
  { code: 'O10', name: 'General / Admiral' }, { code: 'O9', name: 'Lt General / Vice Admiral' },
  { code: 'O8', name: 'Maj General / Rear Admiral (UH)' }, { code: 'O7', name: 'Brig General / Rear Admiral (LH)' },
  { code: 'O6', name: 'Colonel / Captain (N)' }, { code: 'O5', name: 'Lt Colonel / Commander' },
  { code: 'O4', name: 'Major / Lt Commander' }, { code: 'O3', name: 'Captain / Lieutenant' },
  { code: 'O2', name: '1st Lt / Lt (jg)' }, { code: 'O1', name: '2nd Lt / Ensign' },
  { code: 'W5', name: 'Chief Warrant 5' }, { code: 'W4', name: 'Chief Warrant 4' },
  { code: 'W3', name: 'Chief Warrant 3' }, { code: 'W2', name: 'Chief Warrant 2' },
  { code: 'W1', name: 'Warrant Officer 1' },
  { code: 'E9', name: 'Sergeant Major / Master Chief' }, { code: 'E8', name: 'Master Sergeant / Senior Chief' },
  { code: 'E7', name: 'Sergeant 1st Class / Chief Petty Officer' }, { code: 'E6', name: 'Staff Sergeant / Petty Officer 1st' },
];

const CIVILIAN_GRADES = [
  { code: 'EX1', name: 'Executive Level I (Cabinet Secretary)' }, { code: 'EX2', name: 'Executive Level II (Deputy Secretary)' },
  { code: 'EX3', name: 'Executive Level III (Under Secretary)' }, { code: 'EX4', name: 'Executive Level IV (Assistant Secretary)' },
  { code: 'EX5', name: 'Executive Level V' },
  { code: 'SES3', name: 'Senior Executive Service (ES-3)' }, { code: 'SES2', name: 'Senior Executive Service (ES-2)' },
  { code: 'SES1', name: 'Senior Executive Service (ES-1)' }, { code: 'SES', name: 'Senior Executive Service' },
  { code: 'GS15', name: 'GS-15' }, { code: 'GS14', name: 'GS-14' }, { code: 'GS13', name: 'GS-13' },
  { code: 'GS12', name: 'GS-12' }, { code: 'GS11', name: 'GS-11' }, { code: 'GS9', name: 'GS-9' },
  { code: 'GS7', name: 'GS-7' }, { code: 'GS5', name: 'GS-5' },
];

const field = {
  label: { color: '#94A3B8', fontSize: '11px', marginBottom: '3px', display: 'block' },
  input: { background: '#0A0F1C', border: '1px solid #1E2D45', color: '#F1F5F9', padding: '6px 10px', borderRadius: '4px', fontSize: '12px', width: '100%', boxSizing: 'border-box' },
  select: { background: '#0A0F1C', border: '1px solid #1E2D45', color: '#F1F5F9', padding: '6px', borderRadius: '4px', fontSize: '12px', width: '100%' },
  group: { marginBottom: '12px' },
  critical: { backgroundColor: '#0A0F1C', padding: '12px', borderRadius: '6px', border: '1px solid #22D3EE', marginBottom: '16px' },
};

export default function ContactModal({ contact, organizations, functionAreas, onSuccess, onClose }) {
  const isEdit = !!contact?.id;
  const [form, setForm] = useState({
    first_name: '', last_name: '', middle_initial: '', title: '',
    organization_id: '', position_type: 'staff', function_areas: [],
    rank_code: '', rank_name: '', service_branch: '',
    grade_code: '', grade_name: '', political_level: '',
    chamber: '', state_represented: '', district: '', party_affiliation: '',
    committee_assignments: [],
    relationship_strength: 5, relationship_stage: 'identified',
    email: '', phone: '', secondary_email: '', linkedin_url: '',
    next_action: '', next_action_date: '',
    bio: '', notes: '', tags: [],
    ...(contact ? {
      ...contact,
      function_areas: contact.function_areas || [],
      committee_assignments: contact.committee_assignments || [],
      tags: contact.tags || [],
    } : {}),
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('basic');
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Auto-save to sessionStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (unsavedChanges) {
        sessionStorage.setItem(`contactFormDraft_${contact?.id || 'new'}`, JSON.stringify(form));
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [form, unsavedChanges, contact?.id]);

  const set = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setUnsavedChanges(true);
  };

  const validateForm = () => {
    const errors = {};

    if (!form.first_name?.trim()) errors.first_name = 'First name required';
    if (!form.last_name?.trim()) errors.last_name = 'Last name required';

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = 'Invalid email format';
    }

    if (form.phone && !/^[\d\s+\-()]+$/.test(form.phone)) {
      errors.phone = 'Invalid phone format';
    }

    if (form.secondary_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.secondary_email)) {
      errors.secondary_email = 'Invalid email format';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const toggleFA = (fa) => {
    set('function_areas', form.function_areas.includes(fa)
      ? form.function_areas.filter(f => f !== fa)
      : [...form.function_areas, fa]
    );
  };

  const handleRankChange = (code) => {
    const rank = MILITARY_RANKS.find(r => r.code === code);
    set('rank_code', code);
    set('rank_name', rank?.name || '');
  };

  const handleGradeChange = (code) => {
    const grade = CIVILIAN_GRADES.find(g => g.code === code);
    set('grade_code', code);
    set('grade_name', grade?.name || '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      if (isEdit) {
        await crmService.updateContact(contact.id, form);
      } else {
        await crmService.createContact(form);
      }
      sessionStorage.removeItem(`contactFormDraft_${contact?.id || 'new'}`);
      setUnsavedChanges(false);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const getTabCompletion = (tabName) => {
    const criticalFields = {
      basic: ['first_name', 'last_name'],
      classification: ['position_type'],
      contact: ['email'],
      notes: [],
    };

    const required = criticalFields[tabName] || [];
    const filled = required.filter(f => form[f]);
    return filled.length === required.length && required.length > 0;
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: '#111827', border: '1px solid #1E2D45', borderRadius: '8px',
        width: 'min(680px, 95vw)', maxHeight: '95vh', display: 'flex', flexDirection: 'column',
        color: '#F1F5F9', margin: '0 auto',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #1E2D45', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '15px', color: '#22D3EE' }}>
              {isEdit ? 'Edit Contact' : 'Add Contact'}
            </h3>
            {unsavedChanges && (
              <div style={{ fontSize: '11px', color: '#F59E0B' }}>
                ⚠ Unsaved changes (auto-saving…)
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: '18px' }}>×</button>
        </div>

        {/* Tabs with completion indicator */}
        <div style={{ display: 'flex', borderBottom: '1px solid #1E2D45', backgroundColor: '#0A0F1C', overflowX: 'auto' }}>
          {['basic','classification','contact','notes'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '8px 16px', background: 'none', border: 'none',
              borderBottom: tab === t ? '2px solid #22D3EE' : '2px solid transparent',
              color: tab === t ? '#22D3EE' : '#6B7280', cursor: 'pointer', fontSize: '12px',
              textTransform: 'capitalize', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
            }}>
              {t}
              {getTabCompletion(t) && <span style={{ fontSize: '10px', color: '#10B981' }}>✓</span>}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {tab === 'basic' && (
            <>
              {/* Photo Upload */}
              <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid #1E2D45' }}>
                <PhotoUpload
                  contactId={contact?.id}
                  currentPhoto={form.photo_url}
                  firstName={form.first_name}
                  lastName={form.last_name}
                  onPhotoUpdated={(url) => set('photo_url', url)}
                />
              </div>

              {/* Critical fields highlighted */}
              <div style={field.critical}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={field.group}>
                    <label style={field.label}>First Name *</label>
                    <input
                      style={{
                        ...field.input,
                        borderColor: validationErrors.first_name ? '#EF4444' : '#1E2D45',
                      }}
                      value={form.first_name}
                      onChange={e => set('first_name', e.target.value)}
                      required
                    />
                    {validationErrors.first_name && (
                      <div style={{ fontSize: '10px', color: '#EF4444', marginTop: '3px' }}>
                        {validationErrors.first_name}
                      </div>
                    )}
                  </div>
                  <div style={field.group}>
                    <label style={field.label}>Last Name *</label>
                    <input
                      style={{
                        ...field.input,
                        borderColor: validationErrors.last_name ? '#EF4444' : '#1E2D45',
                      }}
                      value={form.last_name}
                      onChange={e => set('last_name', e.target.value)}
                      required
                    />
                    {validationErrors.last_name && (
                      <div style={{ fontSize: '10px', color: '#EF4444', marginTop: '3px' }}>
                        {validationErrors.last_name}
                      </div>
                    )}
                  </div>
                </div>

                <div style={field.group}>
                  <label style={field.label}>Title / Position</label>
                  <input
                    style={field.input}
                    value={form.title || ''}
                    onChange={e => set('title', e.target.value)}
                    placeholder="e.g. Program Executive Officer"
                  />
                </div>

                <div style={field.group}>
                  <label style={field.label}>Organization</label>
                  <SearchableSelect
                    options={[{ value: '', label: '— No organization —' }, ...organizations.map(o => ({
                      value: o.id,
                      label: `${o.acronym ? o.acronym + ' — ' : ''}${o.name}`,
                    }))]}
                    value={form.organization_id || ''}
                    onChange={(value) => set('organization_id', value ? parseInt(value) : null)}
                    placeholder="Search organization"
                  />
                </div>
              </div>

              {/* Optional fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={field.group}>
                  <label style={field.label}>Relationship Stage</label>
                  <select style={field.select} value={form.relationship_stage} onChange={e => set('relationship_stage', e.target.value)}>
                    {['unknown','identified','contacted','engaged','trusted','champion','lost'].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div style={field.group}>
                  <label style={field.label}>Relationship Strength (1-10): {form.relationship_strength}</label>
                  <input type="range" min={1} max={10} value={form.relationship_strength}
                    onChange={e => set('relationship_strength', parseInt(e.target.value))}
                    style={{ width: '100%' }} />
                </div>
              </div>
            </>
          )}

          {tab === 'classification' && (
            <>
              <div style={field.group}>
                <label style={field.label}>Position Type</label>
                <select style={field.select} value={form.position_type} onChange={e => set('position_type', e.target.value)}>
                  {['end_user','decision_maker','process_owner','staff','commander','elected_official',
                    'political_appointee','congressional_staff','contractor_support','researcher','advisor','liaison']
                    .map(p => <option key={p} value={p}>{p.replace(/_/g,' ')}</option>)}
                </select>
              </div>

              <div style={field.group}>
                <label style={field.label}>Functional Areas</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {functionAreas.map(fa => (
                    <button type="button" key={fa} onClick={() => toggleFA(fa)} style={{
                      padding: '6px 12px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer',
                      border: '1px solid #1E2D45',
                      background: form.function_areas.includes(fa) ? '#22D3EE' : '#111827',
                      color: form.function_areas.includes(fa) ? '#0A0F1C' : '#94A3B8',
                      minHeight: '32px',
                      touchAction: 'manipulation',
                    }}>
                      {fa}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ borderTop: '1px solid #1E2D45', marginTop: '12px', paddingTop: '12px' }}>
                <label style={{ ...field.label, fontSize: '12px', color: '#22D3EE', marginBottom: '8px' }}>Military</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={field.group}>
                    <label style={field.label}>Rank</label>
                    <SearchableSelect
                      options={[{ value: '', label: '— None —' }, ...MILITARY_RANKS.map(r => ({
                        value: r.code,
                        label: `${r.code} — ${r.name}`,
                      }))]}
                      value={form.rank_code || ''}
                      onChange={handleRankChange}
                      placeholder="Search rank"
                    />
                  </div>
                  <div style={field.group}>
                    <label style={field.label}>Service Branch</label>
                    <select style={field.select} value={form.service_branch || ''} onChange={e => set('service_branch', e.target.value)}>
                      <option value="">— None —</option>
                      {['army','navy','air_force','marine_corps','space_force','coast_guard','national_guard','joint','reserve']
                        .map(b => <option key={b} value={b}>{b.replace(/_/g,' ')}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div style={{ borderTop: '1px solid #1E2D45', marginTop: '12px', paddingTop: '12px' }}>
                <label style={{ ...field.label, fontSize: '12px', color: '#22D3EE', marginBottom: '8px' }}>Civilian / Political Appointee</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={field.group}>
                    <label style={field.label}>Grade</label>
                    <SearchableSelect
                      options={[{ value: '', label: '— None —' }, ...CIVILIAN_GRADES.map(g => ({
                        value: g.code,
                        label: `${g.code} — ${g.name}`,
                      }))]}
                      value={form.grade_code || ''}
                      onChange={handleGradeChange}
                      placeholder="Search grade"
                    />
                  </div>
                  <div style={field.group}>
                    <label style={field.label}>Political Level</label>
                    <select style={field.select} value={form.political_level || ''} onChange={e => set('political_level', e.target.value)}>
                      <option value="">— Not applicable —</option>
                      {['cabinet','deputy_secretary','under_secretary','assistant_secretary','schedule_c','pta','none']
                        .map(l => <option key={l} value={l}>{l.replace(/_/g,' ')}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div style={{ borderTop: '1px solid #1E2D45', marginTop: '12px', paddingTop: '12px' }}>
                <label style={{ ...field.label, fontSize: '12px', color: '#22D3EE', marginBottom: '8px' }}>Congressional</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={field.group}>
                    <label style={field.label}>Chamber</label>
                    <select style={field.select} value={form.chamber || ''} onChange={e => set('chamber', e.target.value)}>
                      <option value="">— N/A —</option>
                      <option value="senate">Senate</option>
                      <option value="house">House</option>
                    </select>
                  </div>
                  <div style={field.group}>
                    <label style={field.label}>State</label>
                    <input style={field.input} value={form.state_represented || ''} onChange={e => set('state_represented', e.target.value)} placeholder="e.g. Virginia" />
                  </div>
                  <div style={field.group}>
                    <label style={field.label}>Party</label>
                    <select style={field.select} value={form.party_affiliation || ''} onChange={e => set('party_affiliation', e.target.value)}>
                      <option value="">— N/A —</option>
                      <option value="republican">Republican</option>
                      <option value="democrat">Democrat</option>
                      <option value="independent">Independent</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}

          {tab === 'contact' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={field.group}>
                  <label style={field.label}>Work Email</label>
                  <input
                    type="email"
                    style={{
                      ...field.input,
                      borderColor: validationErrors.email ? '#EF4444' : '#1E2D45',
                    }}
                    value={form.email || ''}
                    onChange={e => set('email', e.target.value)}
                  />
                  {validationErrors.email && (
                    <div style={{ fontSize: '10px', color: '#EF4444', marginTop: '3px' }}>
                      {validationErrors.email}
                    </div>
                  )}
                </div>
                <div style={field.group}>
                  <label style={field.label}>Personal / Alt Email</label>
                  <input
                    type="email"
                    style={{
                      ...field.input,
                      borderColor: validationErrors.secondary_email ? '#EF4444' : '#1E2D45',
                    }}
                    value={form.secondary_email || ''}
                    onChange={e => set('secondary_email', e.target.value)}
                  />
                  {validationErrors.secondary_email && (
                    <div style={{ fontSize: '10px', color: '#EF4444', marginTop: '3px' }}>
                      {validationErrors.secondary_email}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={field.group}>
                  <label style={field.label}>Phone</label>
                  <input
                    style={{
                      ...field.input,
                      borderColor: validationErrors.phone ? '#EF4444' : '#1E2D45',
                    }}
                    value={form.phone || ''}
                    onChange={e => set('phone', e.target.value)}
                    placeholder="+1 (703) 555-0100"
                  />
                  {validationErrors.phone && (
                    <div style={{ fontSize: '10px', color: '#EF4444', marginTop: '3px' }}>
                      {validationErrors.phone}
                    </div>
                  )}
                </div>
                <div style={field.group}>
                  <label style={field.label}>LinkedIn URL</label>
                  <input style={field.input} value={form.linkedin_url || ''} onChange={e => set('linkedin_url', e.target.value)} placeholder="https://linkedin.com/in/..." />
                </div>
              </div>
              <div style={field.group}>
                <label style={field.label}>Next Action</label>
                <input style={field.input} value={form.next_action || ''} onChange={e => set('next_action', e.target.value)} placeholder="e.g. Schedule demo meeting" />
              </div>
              <div style={field.group}>
                <label style={field.label}>Due Date</label>
                <input type="date" style={field.input} value={form.next_action_date || ''} onChange={e => set('next_action_date', e.target.value)} />
              </div>
            </>
          )}

          {tab === 'notes' && (
            <>
              <div style={field.group}>
                <label style={field.label}>Bio / Background</label>
                <textarea style={{ ...field.input, minHeight: '100px', resize: 'vertical' }}
                  value={form.bio || ''} onChange={e => set('bio', e.target.value)}
                  placeholder="Career background, areas of interest, previous positions…" />
              </div>
              <div style={field.group}>
                <label style={field.label}>Notes</label>
                <textarea style={{ ...field.input, minHeight: '120px', resize: 'vertical' }}
                  value={form.notes || ''} onChange={e => set('notes', e.target.value)}
                  placeholder="Relationship notes, key interests, approach strategy…" />
              </div>
            </>
          )}

          {error && <div style={{ color: '#EF4444', fontSize: '12px', marginTop: '8px', padding: '8px', backgroundColor: '#EF44441A', borderRadius: '4px' }}>{error}</div>}
        </form>

        <div style={{ padding: '12px 20px', borderTop: '1px solid #1E2D45', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose}
            style={{ padding: '8px 16px', background: '#111827', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', minHeight: '32px', touchAction: 'manipulation' }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding: '8px 16px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', opacity: saving ? 0.6 : 1, minHeight: '32px', touchAction: 'manipulation' }}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Contact'}
          </button>
        </div>
      </div>
    </div>
  );
}
